/**
 * OrgSignup.jsx
 *
 * Organization admin self-registration page.
 * Styling mirrors the Spain-regularization Signup.js exactly:
 *  - same dark theme (#0C1420 background)
 *  - same CSS variables (--card, --border, --blue, --white …)
 *  - same header / step-pills / input / button class names from Signup.css
 *
 * Flow
 * ----
 * Token validated on load via ?ref=PARTNER_TOKEN (GET /partners/by-token).
 * Step 1 → Your details (name, phone)
 * Step 2 → Verify phone (OTP)
 * Step 3 → Work email (+ optional Google)
 * After signup → go to shared login (phone / Google / Apple; no password)
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  orgAuthApi,
  mvpApi,
  getGoogleAuthUrl,
} from '../services/api';
import PhoneDialNationalInput from '../applicant/components/ui/PhoneDialNationalInput';
import { isValidRequiredPhoneParts } from '../applicant/utils/contactFieldValidation';
import { formatApiDetail } from '../utils/apiError';
import { LOGIN_PATH } from '../utils/postLoginNavigation';
import './Signup.css';      // exact same stylesheet as Spain-reg signup
import './OrgSignup.css';   // small supplement for org-only elements

// ---------------------------------------------------------------------------
// Icons (same as Signup.js)
// ---------------------------------------------------------------------------

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" fill="none" aria-hidden>
      <path d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.6 7 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.9z" fill="#FFC107"/>
      <path d="M6.3 14.7l6.6 4.8C14.7 16 19.1 12 24 12c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.6 7 29.6 4 24 4 16.3 4 9.7 8.4 6.3 14.7z" fill="#FF3D00"/>
      <path d="M24 44c5.4 0 10.3-2 14-5.2l-6.5-5.5C29.5 35.1 26.9 36 24 36c-5.3 0-9.7-3.3-11.3-8H6.3C9.6 35.6 16.2 44 24 44z" fill="#4CAF50"/>
      <path d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.5 5.5C37.4 38.5 44 33.5 44 24c0-1.3-.1-2.7-.4-3.9z" fill="#1976D2"/>
    </svg>
  );
}

const STEP_LABELS = ['Your details', 'Verify phone', 'Work email'];
const TOTAL_STEPS = 3;
const STEP_OTP = 2;
const STEP_EMAIL = 3;

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

function buildFullPhone(countryCode, nationalNumber) {
  const cc = String(countryCode || '+34').trim();
  const nat = digitsOnly(nationalNumber);
  if (!nat) return '';
  return `${cc}${nat}`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function OrgSignup() {
  const [searchParams] = useSearchParams();

  const ref = searchParams.get('ref') || '';
  const inviteToken = searchParams.get('token') || '';

  // Org info
  const [tokenError, setTokenError] = useState('');
  const [tokenLoading, setTokenLoading] = useState(true);

  // Steps: 0 = loading/error, 1-3 = form steps, 4 = account created → go sign in
  const [step, setStep] = useState(0);
  const [signupComplete, setSignupComplete] = useState(false);

  // Step 1 fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+34');
  const [nationalNumber, setNationalNumber] = useState('');
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [phoneOtpToken, setPhoneOtpToken] = useState('');

  // Step 2 (OTP)
  const [otpCode, setOtpCode] = useState('');
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');

  // Form state
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ---------------------------------------------------------------------------
  // Validate ref / invite on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!ref && !inviteToken) {
      setTokenError('No signup link found. Please use the link shared by your organisation.');
      setTokenLoading(false);
      return;
    }

    const req = ref
      ? mvpApi.getPartnerByToken(ref)
      : orgAuthApi.validateInvite(inviteToken);

    req
      .then((res) => {
        const data = res.data;
        if (data.prefilled_email) setEmail(data.prefilled_email);
        setStep(1);
      })
      .catch((err) => {
        setTokenError(
          formatApiDetail(err?.response?.data?.detail) ||
          'This signup link is invalid or has expired. Please request a new one.'
        );
      })
      .finally(() => setTokenLoading(false));
  }, [ref, inviteToken]);

  useEffect(() => {
    if (step !== STEP_OTP || otpResendTimer <= 0) return undefined;
    const interval = setInterval(() => {
      setOtpResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [step, otpResendTimer]);

  const fullPhone = useMemo(
    () => buildFullPhone(countryCode, nationalNumber),
    [countryCode, nationalNumber]
  );

  function clearPhoneVerificationIfChanged(nextFull) {
    if (nextFull !== verifiedPhone) {
      setPhoneOtpToken('');
      setVerifiedPhone('');
    }
  }

  function handleCountryCodeChange(code) {
    setCountryCode(code);
    clearPhoneVerificationIfChanged(buildFullPhone(code, nationalNumber));
  }

  function handleNationalNumberChange(value) {
    setNationalNumber(value);
    clearPhoneVerificationIfChanged(buildFullPhone(countryCode, value));
  }

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  function validateStep(s) {
    const errors = {};
    if (s === 1) {
      if (!firstName.trim()) errors.firstName = 'First name is required.';
      if (!lastName.trim()) errors.lastName = 'Last name is required.';
      if (!nationalNumber.trim()) {
        errors.phone = 'Phone number is required.';
      } else       if (!isValidRequiredPhoneParts(countryCode, nationalNumber)) {
        errors.phone = 'Enter a valid phone number for your country.';
      }
    }
    if (s === STEP_EMAIL) {
      if (!email.trim()) {
        errors.email = 'Email is required.';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.email = 'Enter a valid email address.';
      }
    }
    return errors;
  }

  async function sendPhoneOtp() {
    setOtpError('');
    setOtpLoading(true);
    try {
      await orgAuthApi.requestPhoneOtp(fullPhone);
      setOtpResendTimer(30);
      setStep(STEP_OTP);
    } catch (err) {
      setOtpError(
        formatApiDetail(err?.response?.data?.detail) ||
        'Could not send verification code. Please check your number and try again.'
      );
    } finally {
      setOtpLoading(false);
    }
  }

  async function handleContinueFromDetails() {
    const errors = validateStep(1);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setOtpError('');

    if (verifiedPhone === fullPhone && phoneOtpToken) {
      setStep(STEP_EMAIL);
      return;
    }

    setPhoneOtpToken('');
    setVerifiedPhone('');
    setOtpCode('');
    await sendPhoneOtp();
  }

  async function handleVerifyOtp() {
    const code = (otpCode || '').trim();
    if (!code) {
      setOtpError('Enter the verification code.');
      return;
    }
    setOtpError('');
    setOtpLoading(true);
    try {
      const res = await orgAuthApi.verifyPhoneOtp(fullPhone, code);
      setPhoneOtpToken(res.data.phone_otp_token);
      setVerifiedPhone(res.data.phone || fullPhone);
      setStep(STEP_EMAIL);
    } catch (err) {
      setOtpError(formatApiDetail(err?.response?.data?.detail) || 'Invalid or expired code.');
    } finally {
      setOtpLoading(false);
    }
  }

  async function handleResendOtp() {
    if (otpResendTimer > 0 || otpLoading) return;
    setOtpCode('');
    setOtpError('');
    setOtpLoading(true);
    try {
      await orgAuthApi.requestPhoneOtp(fullPhone);
      setOtpResendTimer(30);
    } catch (err) {
      setOtpError(
        formatApiDetail(err?.response?.data?.detail) ||
        'Could not resend verification code. Please try again.'
      );
    } finally {
      setOtpLoading(false);
    }
  }

  function next() {
    if (step === 1) {
      handleContinueFromDetails();
      return;
    }
    const errors = validateStep(step);
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setStep((s) => s + 1);
  }

  function back() {
    setFieldErrors({});
    setSubmitError('');
    setOtpError('');
    setStep((s) => s - 1);
  }

  function handleOtpKeyDown(e) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (!otpLoading) handleVerifyOtp();
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async function handleSubmit(e) {
    e.preventDefault();
    const errors = validateStep(STEP_EMAIL);
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }

    setSubmitting(true);
    setSubmitError('');
    try {
      await orgAuthApi.signup({
        ref: ref || undefined,
        invite_token: inviteToken || undefined,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim().toLowerCase(),
        phone: verifiedPhone || fullPhone,
        phone_otp_token: phoneOtpToken,
      });
      // Account is ready - LP signs in next via phone OTP (or Google/Apple).
      setSignupComplete(true);
      setStep(4);
    } catch (err) {
      setSubmitError(
        formatApiDetail(err?.response?.data?.detail) ||
        'Something went wrong. Please try again or contact support.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleGoogleSignup() {
    if (ref) sessionStorage.setItem('org_signup_ref', ref);
    if (inviteToken) sessionStorage.setItem('org_invite_token', inviteToken);
    window.location.href = getGoogleAuthUrl('org_signup');
  }

  // ---------------------------------------------------------------------------
  // Render states
  // ---------------------------------------------------------------------------

  // Loading spinner - same look as the rest of the dark app
  if (tokenLoading) {
    return (
      <div className="signup-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="org-signup-spinner" aria-label="Loading" />
      </div>
    );
  }

  // Invalid link error
  if (tokenError) {
    return (
      <div className="signup-page">
        <header className="signup-header">
          <div className="signup-header-inner">
            <div className="logo-lockup">
              <div className="logo-pill">
                <img src="/header-logo.png" alt="Global Mobility" className="logo-pill-image" />
              </div>
              <div className="logo-wordmark">
                <span className="logo-primary">GLOBAL MOBILITY</span>
              </div>
            </div>
          </div>
        </header>
        <div className="signup-main" style={{ marginTop: 48, textAlign: 'center' }}>
          <p style={{ fontSize: '2rem', marginBottom: 16 }}>⚠️</p>
          <h2 className="signup-heading" style={{ fontSize: '1.4rem' }}>Invalid signup link</h2>
          <p className="signup-desc">{tokenError}</p>
          <a href="/login" className="signup-btn-primary" style={{ display: 'inline-block', textDecoration: 'none', lineHeight: '52px', padding: '0 32px', width: 'auto' }}>
            Go to sign in
          </a>
        </div>
      </div>
    );
  }

  // Account created - send LP to the shared login page
  if (signupComplete || step === 4) {
    return (
      <div className="signup-page">
        <header className="signup-header">
          <div className="signup-header-inner">
            <div className="logo-lockup">
              <div className="logo-pill">
                <img src="/header-logo.png" alt="Global Mobility" className="logo-pill-image" />
              </div>
              <div className="logo-wordmark">
                <span className="logo-primary">GLOBAL MOBILITY</span>
                <span className="logo-sub">LP Workbench</span>
              </div>
            </div>
          </div>
        </header>
        <div className="signup-main" style={{ marginTop: 48, textAlign: 'center' }}>
          <h2 className="signup-heading" style={{ fontSize: '1.4rem' }}>Account created</h2>
          <p className="signup-desc">
            Your organization admin account is ready. Sign in with your phone (or Google / Apple) to open your Slate.
          </p>
          <a
            href={LOGIN_PATH}
            className="signup-btn-primary"
            style={{ display: 'inline-block', textDecoration: 'none', lineHeight: '52px', padding: '0 32px', width: 'auto' }}
          >
            Go to sign in
          </a>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Full form
  // ---------------------------------------------------------------------------

  return (
    <div className="signup-page">
      {/* ── Header (identical to Signup.js) ── */}
      <header className="signup-header">
        <div className="signup-header-inner">
          <div className="logo-lockup">
            <div className="logo-pill">
              <img src="/header-logo.png" alt="Global Mobility" className="logo-pill-image" />
            </div>
            <div className="logo-wordmark">
              <span className="logo-primary">GLOBAL MOBILITY</span>
              <span className="logo-sub">LP Workbench</span>
            </div>
          </div>
        </div>
      </header>

      <main className="signup-main">
        {/* Top bar */}
        <div className="signup-topbar">
          {step > 1 && (
            <button
              type="button"
              className="signup-back-btn"
              onClick={back}
              aria-label="Go back"
            >
              ←
            </button>
          )}
          <span className="signup-topbar-label">
            {step > 1 ? STEP_LABELS[step - 1] : 'Create admin account'}
          </span>
        </div>

        {/* Step pills - same as Signup.js */}
        <div className="signup-step-pills" role="list" aria-label="Progress">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              role="listitem"
              className={`signup-step-pill${i < step - 1 ? ' done' : ''}${i === step - 1 ? ' active' : ''}`}
            />
          ))}
        </div>

        {/* ── Step 1: Your details ── */}
        {step === 1 && (
          <div>
            <h1 className="signup-heading">
              Join <em>GLOBAL MOBILITY</em>
            </h1>
            <p className="signup-desc">
              You've been invited to manage <strong style={{ color: 'var(--white)' }}>GLOBAL MOBILITY</strong> on
              the Global Mobility platform. Set up your admin account to get started.
            </p>

            {/* Name row */}
            <div className="org-signup-name-row">
              <div className="org-signup-field">
                <label className="org-signup-label" htmlFor="firstName">First name *</label>
                <input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  className={`signup-national-input org-signup-input${fieldErrors.firstName ? ' signup-national-input--invalid' : ''}`}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Maria"
                />
                {fieldErrors.firstName && <span className="org-signup-field-error">{fieldErrors.firstName}</span>}
              </div>
              <div className="org-signup-field">
                <label className="org-signup-label" htmlFor="lastName">Last name *</label>
                <input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  className={`signup-national-input org-signup-input${fieldErrors.lastName ? ' signup-national-input--invalid' : ''}`}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="García"
                />
                {fieldErrors.lastName && <span className="org-signup-field-error">{fieldErrors.lastName}</span>}
              </div>
            </div>

            <div className="org-signup-field">
              <label className="org-signup-label" htmlFor="phone">Phone *</label>
              <PhoneDialNationalInput
                id="phone"
                theme="dark"
                countryCode={countryCode}
                onCountryCodeChange={handleCountryCodeChange}
                nationalNumber={nationalNumber}
                onNationalNumberChange={handleNationalNumberChange}
                placeholder="612 345 678"
                showFilledState={false}
                invalid={Boolean(fieldErrors.phone)}
              />
              {fieldErrors.phone && <span className="org-signup-field-error">{fieldErrors.phone}</span>}
            </div>

            {otpError && step === 1 && (
              <p className="signup-error" role="alert">{otpError}</p>
            )}

            <button
              type="button"
              className="signup-btn-primary"
              onClick={next}
              disabled={otpLoading}
            >
              {otpLoading ? 'Sending code…' : 'Continue'}
            </button>
          </div>
        )}

        {/* ── Step 2: Phone OTP (same flow as Spain regularization) ── */}
        {step === STEP_OTP && (
          <div>
            <h1 className="signup-heading">Enter verification code</h1>
            <p className="signup-desc">
              We sent a one-time code by SMS to{' '}
              <strong style={{ color: 'var(--white)' }}>{fullPhone}</strong>.
            </p>
            <input
              type="text"
              className="signup-otp-input"
              placeholder="6-digit code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={handleOtpKeyDown}
              maxLength={6}
              autoComplete="one-time-code"
            />
            <button
              type="button"
              className="signup-btn-primary"
              onClick={handleVerifyOtp}
              disabled={otpLoading}
            >
              {otpLoading ? 'Verifying…' : 'Continue'}
            </button>
            <p className="signup-otp-resend-timer">
              {otpResendTimer > 0 ? (
                <>
                  <span>Resend available in </span>
                  <span className="notranslate" translate="no">{otpResendTimer}s</span>
                </>
              ) : (
                'You can request a new code now.'
              )}
            </p>
            {otpResendTimer <= 0 && (
              <button
                type="button"
                className="signup-btn-resend"
                onClick={handleResendOtp}
                disabled={otpLoading}
              >
                {otpLoading ? 'Sending…' : 'Resend code'}
              </button>
            )}
            {otpError && <p className="signup-error" role="alert">{otpError}</p>}
          </div>
        )}

        {/* ── Step 3: Work email ── */}
        {step === STEP_EMAIL && (
          <form onSubmit={handleSubmit} noValidate>
            <h1 className="signup-heading">Add your work email</h1>
            <p className="signup-desc">
              We&apos;ll use this for production updates. Sign in later with your verified phone.
            </p>

            <div className="org-signup-field">
              <label className="org-signup-label" htmlFor="email">Work email *</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={`signup-national-input org-signup-input${fieldErrors.email ? ' signup-national-input--invalid' : ''}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@yourcompany.com"
              />
              {fieldErrors.email && <span className="org-signup-field-error">{fieldErrors.email}</span>}
            </div>

            <div className="signup-social-row">
              <button
                type="button"
                className="signup-btn-social"
                onClick={handleGoogleSignup}
              >
                <GoogleIcon /> Continue with Google
              </button>
            </div>

            {submitError && (
              <p className="signup-error" role="alert">{submitError}</p>
            )}

            <button
              type="submit"
              className="signup-btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Creating account…' : 'Create account'}
            </button>

            <p className="signup-terms">
              By creating an account you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
          </form>
        )}

        <p className="signup-signin-row" style={{ marginTop: 12 }}>
          Already have an account?{' '}
          <a href="/login">Sign in</a>
        </p>
      </main>
    </div>
  );
}
